import styled from 'styled-components';

export const SliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  position: relative;
  ${(props) =>
    props.$valueDisplay === 'tracking-below' && `padding-bottom: 1.5rem;`}
  ${(props) =>
    props.$valueDisplay === 'tracking-above' && `padding-top: 1.5rem;`}
  ${(props) =>
    props.$showMinMax &&
    props.$minMaxPosition === 'below' &&
    `padding-bottom: 1.25rem;`}
  ${(props) =>
    props.$showMinMax &&
    props.$minMaxPosition === 'above' &&
    `padding-top: 1.25rem;`}
`;

export const SliderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
`;

export const MinMaxLabel = styled.span`
  position: absolute;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => props.theme.secondaryColorDark};
  white-space: nowrap;

  ${(props) => props.$align === 'left' && `left: 0;`}
  ${(props) => props.$align === 'right' && `right: 0;`}

  ${(props) => props.$position === 'below' && `top: calc(100% + 0.125rem);`}
  ${(props) => props.$position === 'above' && `bottom: calc(100% + 0.125rem);`}

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    ${(props) =>
      props.$position === 'below' &&
      (props.$tickVisible
        ? `top: calc(100% + 1.25rem);`
        : `top: calc(100% + 0.5rem);`)}
    ${(props) =>
      props.$position === 'above' &&
      (props.$tickVisible
        ? `bottom: calc(100% + 1.25rem);`
        : `bottom: calc(100% + 0.5rem);`)}
  }
`;

export const RangeTrackWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

export const StyledRange = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  outline: none;
  overflow: visible;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  margin: 0.5rem 0;
  position: relative;
  z-index: 2;

  background: transparent;

  &::-webkit-slider-runnable-track {
    height: 6px;
    border-radius: 3px;
    overflow: visible;
    background: ${(props) => {
      if (props.disabled) return props.theme.grayLight;
      const safeVal = props.value ?? props.min ?? 0;
      const range = props.max - props.min || 1;
      const percent = Math.max(
        0,
        Math.min(100, ((safeVal - props.min) / range) * 100),
      );
      const filledColor = props.$filledTrack
        ? props.theme[props.$filledTrackColor] || props.theme.secondaryColor
        : props.theme.gray;
      return `linear-gradient(to right, ${filledColor} 0%, ${filledColor} ${percent}%, ${props.theme.gray} ${percent}%, ${props.theme.gray} 100%)`;
    }};
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    position: relative;
    z-index: 2;
    transform: translateZ(0);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${(props) =>
      props.disabled
        ? props.theme.disabledColor
        : props.theme[props.$trackColor] || props.theme.accentColor};
    border: 1.5px solid ${(props) => props.theme.white};
    box-shadow: 0 1px 3px ${(props) => props.theme.overlayDark};
    margin-top: -7px;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  }

  &::-moz-range-track {
    height: 6px;
    border-radius: 3px;
    background: transparent;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${(props) =>
      props.disabled
        ? props.theme.disabledColor
        : props.theme[props.$trackColor] || props.theme.accentColor};
    border: 1.5px solid ${(props) => props.theme.white};
    box-shadow: 0 1px 3px ${(props) => props.theme.overlayDark};
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  }

  &::-moz-range-progress {
    background: ${(props) =>
      props.$filledTrack
        ? props.theme[props.$filledTrackColor] || props.theme.secondaryColor
        : 'transparent'};
    height: 6px;
    border-radius: 3px;
  }

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    &::-webkit-slider-thumb {
      width: 35px;
      height: 35px;
      margin-top: -14.5px;
    }
  }
`;

export const ValueDisplay = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: ${(props) =>
    props.theme[props.$trackColor] || props.theme.accentColor};
  white-space: nowrap;
  pointer-events: none;
  display: inline-block;
  position: relative;

  ${(props) =>
    props.$mode === 'below-center' &&
    `
    text-align: center;
    margin-top: 0.5rem;
  `}

  ${(props) =>
    props.$mode === 'tracking-below' &&
    `
    position: absolute;
    top: calc(100% + 0.25rem);
    left: calc(10px + (100% - 20px) * ${props.$trackingLeft / 100});
    transform: translateX(-50%);
  `}

  ${(props) =>
    props.$mode === 'tracking-above' &&
    `
    position: absolute;
    bottom: calc(100% + 0.25rem);
    left: calc(10px + (100% - 20px) * ${props.$trackingLeft / 100});
    transform: translateX(-50%);
  `}

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    ${(props) =>
      props.$mode === 'below-center' &&
      `
      margin-top: 1rem;
    `}

    ${(props) =>
      props.$mode === 'tracking-below' &&
      `
      top: calc(100% + 0.75rem);
      left: calc(17.5px + (100% - 35px) * ${props.$trackingLeft / 100});
    `}

    ${(props) =>
      props.$mode === 'tracking-above' &&
      `
      bottom: calc(100% + 0.75rem);
      left: calc(17.5px + (100% - 35px) * ${props.$trackingLeft / 100});
    `}
  }
`;

export const TickMark = styled.div`
  position: absolute;
  width: 0.2rem;
  height: 1rem;
  background: ${(props) => props.theme.grayLight};
  border-radius: 1px;
  pointer-events: none;
  z-index: 1;
  top: 50%;
  transform: translate(-50%, -50%);
  left: calc(10px + (100% - 20px) * ${(props) => props.$percent / 100});
`;

export const TickLabel = styled.span`
  position: absolute;
  font-size: 0.75rem;
  font-weight: 600;

  color: ${(props) => props.theme.grayDark};
  white-space: nowrap;
  pointer-events: none;
  transform: translateX(-50%);
  left: calc(10px + (100% - 20px) * ${(props) => props.$percent / 100});

  ${(props) => props.$position === 'below' && `top: calc(100% + 0.125rem);`}
  ${(props) => props.$position === 'above' && `bottom: calc(100% + 0.125rem);`}

  @media only screen and (max-width: ${(props) => props.theme.breakpointMd}) {
    ${(props) => props.$position === 'below' && `top: calc(100% + 0.5rem);`}
    ${(props) => props.$position === 'above' && `bottom: calc(100% + 0.5rem);`}
  }
`;

export const ValueCenter = styled.span`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 0.5rem;
`;

export const DisabledMessage = styled.p`
  font-size: ${(p) => p.theme.fontSizeMd};
  color: ${(p) => p.theme.grayDark};
  font-style: italic;
  margin: ${(p) => (p.$hideSlider ? '0' : '1rem')} 0 0 0;
  width: 100%;
`;
